import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signInAnonymously, 
  signOut 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// ✅ Usamos ruta absoluta (jsconfig.json)
import { auth, db } from 'services/firebase'; 

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true); 
      
      if (firebaseUser) {
        setUser(firebaseUser);
        
        // 🚨 LÓGICA DE MODO PRUEBA (Anónimo)
        if (firebaseUser.isAnonymous) {
          const rolElegido = window.sessionStorage.getItem('rolPrueba') || 'padre';
          setRole(rolElegido);
          setLoading(false);
          return; 
        }

        // 🛡️ LÓGICA DE USUARIO REAL (Consulta a Firestore)
        try {
          const docRef = doc(db, 'usuarios', firebaseUser.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setRole(docSnap.data().rol);
          } else {
            setRole('padre'); // Rol por defecto
          }
        } catch (err) {
          console.error("Error de conexión a Firestore:", err);
          setRole('padre'); 
        }
      } else {
        setUser(null);
        setRole(null);
      }
      setLoading(false); 
    });

    return () => unsubscribe();
  }, []);

  // Función de Login con Email y Password
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Credenciales incorrectas. Verifica tu correo y contraseña.');
      setLoading(false);
      throw err; 
    }
  };

  // Función para Modo Prueba
  const loginAnonimo = async (rolSimulado) => {
    setLoading(true);
    setError(null);
    try {
      window.sessionStorage.setItem('rolPrueba', rolSimulado); 
      await signInAnonymously(auth);
    } catch (err) {
      setError("Error al iniciar modo prueba.");
      setLoading(false);
    }
  };

  // Función para Cerrar Sesión
  const logout = async () => {
    setLoading(true);
    try {
      window.sessionStorage.removeItem('rolPrueba');
      await signOut(auth);
    } catch (err) {
      console.error("Error al cerrar sesión:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, role, loading, error, login, loginAnonimo, logout }}>
      {!loading ? children : (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center font-sans">
          <div className="flex flex-col items-center gap-4">
             <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
             <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Validando Credenciales...</p>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};